import { useState, useEffect } from "react";
import { safeEvaluate } from "@/lib/safeEvaluate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, Atom, ArrowLeftRight } from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

interface CalculatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CalculatorDialog = ({ open, onOpenChange }: CalculatorDialogProps) => {
  const { isRtl } = useTranslation();
  const [basicDisplay, setBasicDisplay] = useState("0");
  const [scientificDisplay, setScientificDisplay] = useState("0");
  const [converterFrom, setConverterFrom] = useState("");
  const [converterTo, setConverterTo] = useState("");
  const [converterType, setConverterType] = useState("length");
  const [activeTab, setActiveTab] = useState("basic");
  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard events for Basic and Scientific calculators
      if (activeTab === "converter") return;

      const key = e.key;
      
      // Numbers and operators
      if (/[0-9+\-*/.()]/.test(key)) {
        e.preventDefault();
        if (activeTab === "basic") {
          handleBasicInput(key);
        } else if (activeTab === "scientific") {
          handleScientificInput(key);
        }
      }
      
      // Enter for equals
      if (key === "Enter") {
        e.preventDefault();
        if (activeTab === "basic") {
          handleBasicCalculate();
        } else if (activeTab === "scientific") {
          handleScientificCalculate();
        }
      }
      
      // Backspace to clear last character
      if (key === "Backspace") {
        e.preventDefault();
        if (activeTab === "basic") {
          setBasicDisplay(prev => prev.length > 1 ? prev.slice(0, -1) : "0");
        } else if (activeTab === "scientific") {
          setScientificDisplay(prev => prev.length > 1 ? prev.slice(0, -1) : "0");
        }
      }
      
      // Escape to clear
      if (key === "Escape") {
        e.preventDefault();
        if (activeTab === "basic") {
          handleBasicClear();
        } else if (activeTab === "scientific") {
          handleScientificClear();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, activeTab, basicDisplay, scientificDisplay]);

  // Basic Calculator
  const handleBasicInput = (value: string) => {
    if (basicDisplay === "0" && value !== ".") {
      setBasicDisplay(value);
    } else {
      setBasicDisplay(basicDisplay + value);
    }
  };

  const handleBasicClear = () => setBasicDisplay("0");

  const handleBasicCalculate = () => {
    try {
      const result = safeEvaluate(basicDisplay);
      setBasicDisplay(String(result));
    } catch {
      setBasicDisplay("Error");
    }
  };

  // Scientific Calculator
  const handleScientificInput = (value: string) => {
    if (scientificDisplay === "0" && value !== ".") {
      setScientificDisplay(value);
    } else {
      setScientificDisplay(scientificDisplay + value);
    }
  };

  const handleScientificFunction = (func: string) => {
    try {
      let result: number;
      const value = parseFloat(scientificDisplay);
      
      switch (func) {
        case "sin":
          result = Math.sin(value * Math.PI / 180);
          break;
        case "cos":
          result = Math.cos(value * Math.PI / 180);
          break;
        case "tan":
          result = Math.tan(value * Math.PI / 180);
          break;
        case "sqrt":
          result = Math.sqrt(value);
          break;
        case "square":
          result = value * value;
          break;
        case "log":
          result = Math.log10(value);
          break;
        case "ln":
          result = Math.log(value);
          break;
        default:
          result = value;
      }
      
      setScientificDisplay(String(result));
    } catch {
      setScientificDisplay("Error");
    }
  };

  const handleScientificClear = () => setScientificDisplay("0");

  const handleScientificCalculate = () => {
    try {
      const result = safeEvaluate(scientificDisplay);
      setScientificDisplay(String(result));
    } catch {
      setScientificDisplay("Error");
    }
  };

  // Converter
  const conversionRates: Record<string, Record<string, number>> = {
    length: {
      meter: 1,
      kilometer: 0.001,
      centimeter: 100,
      millimeter: 1000,
      mile: 0.000621371,
      yard: 1.09361,
      foot: 3.28084,
      inch: 39.3701,
    },
    weight: {
      kilogram: 1,
      gram: 1000,
      milligram: 1000000,
      ton: 0.001,
      pound: 2.20462,
      ounce: 35.274,
    },
    temperature: {
      celsius: 1,
      fahrenheit: 1,
      kelvin: 1,
    },
    area: {
      "square meter": 1,
      "square kilometer": 0.000001,
      "square centimeter": 10000,
      "square millimeter": 1000000,
      "square mile": 3.861e-7,
      "square yard": 1.19599,
      "square foot": 10.7639,
      acre: 0.000247105,
      hectare: 0.0001,
    },
    volume: {
      liter: 1,
      milliliter: 1000,
      "cubic meter": 0.001,
      "cubic centimeter": 1000,
      gallon: 0.264172,
      quart: 1.05669,
      pint: 2.11338,
      cup: 4.22675,
      "fluid ounce": 33.814,
    },
    data: {
      byte: 1,
      kilobyte: 0.001,
      megabyte: 0.000001,
      gigabyte: 1e-9,
      terabyte: 1e-12,
      bit: 8,
      kilobit: 0.008,
      megabit: 0.000008,
      gigabit: 8e-9,
    },
    currency: {
      USD: 1,
      SAR: 3.75,
      EUR: 0.92,
      GBP: 0.79,
      JPY: 149.5,
      AED: 3.67,
      KWD: 0.31,
      BHD: 0.38,
      QAR: 3.64,
      OMR: 0.38,
    },
  };

  const [fromUnit, setFromUnit] = useState("meter");
  const [toUnit, setToUnit] = useState("kilometer");

  const handleConvert = () => {
    const value = parseFloat(converterFrom);
    if (isNaN(value)) {
      setConverterTo("Invalid input");
      return;
    }

    if (converterType === "temperature") {
      let result: number;
      if (fromUnit === "celsius" && toUnit === "fahrenheit") {
        result = (value * 9/5) + 32;
      } else if (fromUnit === "celsius" && toUnit === "kelvin") {
        result = value + 273.15;
      } else if (fromUnit === "fahrenheit" && toUnit === "celsius") {
        result = (value - 32) * 5/9;
      } else if (fromUnit === "fahrenheit" && toUnit === "kelvin") {
        result = ((value - 32) * 5/9) + 273.15;
      } else if (fromUnit === "kelvin" && toUnit === "celsius") {
        result = value - 273.15;
      } else if (fromUnit === "kelvin" && toUnit === "fahrenheit") {
        result = ((value - 273.15) * 9/5) + 32;
      } else {
        result = value;
      }
      setConverterTo(result.toFixed(2));
    } else {
      const rates = conversionRates[converterType];
      const baseValue = value / rates[fromUnit];
      const result = baseValue * rates[toUnit];
      setConverterTo(result.toFixed(4));
    }
  };

  const BasicCalculator = () => (
    <div className="space-y-4">
      <Input
        value={basicDisplay}
        readOnly
        className="text-right text-2xl font-semibold h-14 bg-muted"
      />
      <div className="grid grid-cols-4 gap-2">
        {["7", "8", "9", "/"].map((btn) => (
          <Button key={btn} variant="outline" onClick={() => handleBasicInput(btn)}>
            {btn}
          </Button>
        ))}
        {["4", "5", "6", "*"].map((btn) => (
          <Button key={btn} variant="outline" onClick={() => handleBasicInput(btn)}>
            {btn}
          </Button>
        ))}
        {["1", "2", "3", "-"].map((btn) => (
          <Button key={btn} variant="outline" onClick={() => handleBasicInput(btn)}>
            {btn}
          </Button>
        ))}
        {["0", ".", "=", "+"].map((btn) => (
          <Button
            key={btn}
            variant={btn === "=" ? "default" : "outline"}
            onClick={() => (btn === "=" ? handleBasicCalculate() : handleBasicInput(btn))}
          >
            {btn}
          </Button>
        ))}
      </div>
      <Button variant="destructive" className="w-full" onClick={handleBasicClear}>
        Clear
      </Button>
    </div>
  );

  const ScientificCalculator = () => (
    <div className="space-y-4">
      <Input
        value={scientificDisplay}
        readOnly
        className="text-right text-2xl font-semibold h-14 bg-muted"
      />
      <div className="grid grid-cols-4 gap-2">
        {["sin", "cos", "tan", "√"].map((btn) => (
          <Button
            key={btn}
            variant="secondary"
            onClick={() => handleScientificFunction(btn === "√" ? "sqrt" : btn)}
            className="text-xs"
          >
            {btn}
          </Button>
        ))}
        {["x²", "log", "ln", "π"].map((btn) => (
          <Button
            key={btn}
            variant="secondary"
            onClick={() => {
              if (btn === "π") handleScientificInput(String(Math.PI));
              else if (btn === "x²") handleScientificFunction("square");
              else handleScientificFunction(btn);
            }}
            className="text-xs"
          >
            {btn}
          </Button>
        ))}
        {["7", "8", "9", "/"].map((btn) => (
          <Button key={btn} variant="outline" onClick={() => handleScientificInput(btn)}>
            {btn}
          </Button>
        ))}
        {["4", "5", "6", "*"].map((btn) => (
          <Button key={btn} variant="outline" onClick={() => handleScientificInput(btn)}>
            {btn}
          </Button>
        ))}
        {["1", "2", "3", "-"].map((btn) => (
          <Button key={btn} variant="outline" onClick={() => handleScientificInput(btn)}>
            {btn}
          </Button>
        ))}
        {["0", ".", "=", "+"].map((btn) => (
          <Button
            key={btn}
            variant={btn === "=" ? "default" : "outline"}
            onClick={() => (btn === "=" ? handleScientificCalculate() : handleScientificInput(btn))}
          >
            {btn}
          </Button>
        ))}
      </div>
      <Button variant="destructive" className="w-full" onClick={handleScientificClear}>
        Clear
      </Button>
    </div>
  );

  const UnitConverter = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Conversion Type</Label>
        <Select value={converterType} onValueChange={(value) => {
          setConverterType(value);
          setFromUnit(Object.keys(conversionRates[value])[0]);
          setToUnit(Object.keys(conversionRates[value])[1]);
          setConverterFrom("");
          setConverterTo("");
        }}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="length">Length</SelectItem>
            <SelectItem value="weight">Weight</SelectItem>
            <SelectItem value="temperature">Temperature</SelectItem>
            <SelectItem value="area">Area</SelectItem>
            <SelectItem value="volume">Volume</SelectItem>
            <SelectItem value="data">Data Storage</SelectItem>
            <SelectItem value="currency">Currency</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>From</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            value={converterFrom}
            onChange={(e) => setConverterFrom(e.target.value)}
            placeholder="Enter value"
          />
          <Select value={fromUnit} onValueChange={setFromUnit}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(conversionRates[converterType]).map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {unit}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-center">
        <Button variant="ghost" size="icon" onClick={handleConvert}>
          <ArrowLeftRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        <Label>To</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            value={converterTo}
            readOnly
            placeholder="Result"
            className="bg-muted"
          />
          <Select value={toUnit} onValueChange={setToUnit}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(conversionRates[converterType]).map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {unit}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button className="w-full" onClick={handleConvert}>
        Convert
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" dir={isRtl ? "rtl" : "ltr"}>
      
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Calculator Tools
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-2">
            Use keyboard: 0-9, +, -, *, /, Enter (=), Backspace, Esc (clear)
          </p>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic" className="text-xs sm:text-sm">
              <Calculator className="h-4 w-4 mr-1" />
              Basic
            </TabsTrigger>
            <TabsTrigger value="scientific" className="text-xs sm:text-sm">
              <Atom className="h-4 w-4 mr-1" />
              Scientific
            </TabsTrigger>
            <TabsTrigger value="converter" className="text-xs sm:text-sm">
              <ArrowLeftRight className="h-4 w-4 mr-1" />
              Converter
            </TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="mt-4">
            <BasicCalculator />
          </TabsContent>

          <TabsContent value="scientific" className="mt-4">
            <ScientificCalculator />
          </TabsContent>

          <TabsContent value="converter" className="mt-4">
            <UnitConverter />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
